import React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DateTime } from "luxon";

// The compact strip has no arrows — scrolling IS its paging — so `daysToShow`
// would be a hard ceiling on how far ahead it can ever reach. It renders the
// whole bookable window instead, up to this many chips.
const COMPACT_MAX_DAYS = 120;

// `today` (ISO date) lets a view anchor the strip to the club's calendar; without
// it the device's own date is used. `hideBeforeMin` starts the strip at `minDate`
// instead of greying out the days before it — for days that can never be picked
// (a package that hasn't started), not ones that merely aren't open yet. Changing
// `revealKey` brings the selected day back into view even when the value itself
// did not move.
function DateSelector({
  value,
  onChange,
  daysToShow = 7,
  minDate,
  maxDate,
  compact = false,
  dense = false,
  today,
  hideBeforeMin = false,
  revealKey
}) {
  const todayStart = today ? DateTime.fromISO(today).startOf("day") : DateTime.now().startOf("day");
  const minStart = minDate ? DateTime.fromISO(minDate).startOf("day") : null;
  const firstDay = hideBeforeMin && minStart && minStart > todayStart ? minStart : todayStart;
  const selectedDate = value ? DateTime.fromISO(value) : todayStart;

  const [startDate, setStartDate] = React.useState(() => firstDay);

  // A minDate that arrives (or moves) after mount — the page only learns the package's
  // start from its first load — moves the strip's first page with it.
  React.useEffect(() => {
    if (startDate < firstDay) setStartDate(firstDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstDay.toISODate()]);

  // How many days the strip actually renders: one page for the arrowed variant,
  // the rest of the booking window for the scrolling one.
  const visibleDays = React.useMemo(() => {
    if (!compact) return daysToShow;
    if (!maxDate) return COMPACT_MAX_DAYS;
    const span = Math.floor(DateTime.fromISO(maxDate).startOf("day").diff(startDate, "days").days) + 1;
    return Math.min(COMPACT_MAX_DAYS, Math.max(daysToShow, span));
  }, [compact, daysToShow, maxDate, startDate]);

  // Follow a date set from outside — the "jump to" on the package banner, a
  // date carried in the URL, a reset after a filter change. The strip pages
  // itself only through its own arrows, so without this the value moved, the
  // slots below changed, and the strip sat on today's week with nothing
  // selected: the jump looked broken when it had already happened.
  React.useEffect(() => {
    if (!value) return;
    const target = DateTime.fromISO(value).startOf("day");
    if (!target.isValid) return;
    const lastVisible = startDate.plus({ days: visibleDays - 1 });
    if (target >= startDate && target <= lastVisible) return;
    setStartDate(target < firstDay ? firstDay : target);
    // Only when the value itself moves — paging with the arrows must not be
    // dragged back to the selected day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, visibleDays, revealKey]);

  // The compact strip scrolls rather than pages, so a value set from outside can
  // land on a chip that is already rendered but sitting off-screen. Paging never
  // fires (the date IS on this page), and the strip looks like it ignored the
  // jump. Bring the selected day into the middle of the row instead.
  const scrollerRef = React.useRef(null);
  const selectedChipRef = React.useRef(null);

  React.useEffect(() => {
    if (!compact) return;
    const scroller = scrollerRef.current;
    const chip = selectedChipRef.current;
    if (!scroller || !chip) return;
    const centered = chip.offsetLeft - (scroller.clientWidth - chip.offsetWidth) / 2;
    scroller.scrollTo({ left: Math.max(0, centered), behavior: "smooth" });
  }, [compact, value, startDate, visibleDays, revealKey]);

  // Rewind the page when maxDate shrinks below it (e.g. switching to a
  // service with a shorter booking window) — otherwise the strip strands on
  // a page of fully disabled days that only manual back-paging can leave.
  React.useEffect(() => {
    if (maxDate && startDate > DateTime.fromISO(maxDate)) {
      setStartDate(firstDay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDate, startDate]);

  const dates = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < visibleDays; i++) result.push(startDate.plus({ days: i }));
    return result;
  }, [startDate, visibleDays]);

  // "4 – 10 Oct 2026", or "28 Sep – 4 Oct 2026" when the page straddles a month.
  const pageLabel = React.useMemo(() => {
    const first = dates[0];
    const last = dates[dates.length - 1];
    if (!first || !last) return "";
    if (first.hasSame(last, "month")) return `${first.day} – ${last.toFormat("d MMM yyyy")}`;
    return `${first.toFormat("d MMM")} – ${last.toFormat("d MMM yyyy")}`;
  }, [dates]);

  const isSelectable = (date) => {
    if (date < todayStart) return false;
    if (minDate && date < DateTime.fromISO(minDate)) return false;
    if (maxDate && date > DateTime.fromISO(maxDate)) return false;
    return true;
  };

  // Every chip carries its own month. A strip of bare numbers reads "30 31 1 2"
  // with nothing saying where August ended and September began — and a page
  // several arrows deep says nothing about which month it is at all.

  // The chosen day is filled (primary); today is only a ring in the accent
  // (secondary). A filled today read as a second selected day.
  const isSelected = (date) => date.toISODate() === selectedDate.toISODate();
  const isToday = (date) => date.toISODate() === todayStart.toISODate();
  const canGoBack = startDate > firstDay;
  // Paging past maxDate would only show disabled days — stop at the page holding it.
  const canGoNext = !maxDate || startDate.plus({ days: daysToShow }) <= DateTime.fromISO(maxDate);

  const handlePrev = () => {
    const newStart = startDate.minus({ days: daysToShow });
    setStartDate(newStart >= firstDay ? newStart : firstDay);
  };

  const handleNext = () => {
    if (canGoNext) setStartDate(startDate.plus({ days: daysToShow }));
  };

  const handleSelect = (date) => {
    if (isSelectable(date)) onChange(date.toISODate());
  };

  // Compact variant: a single horizontally-scrollable row of small day pills,
  // no chevrons or month heading. Designed for the mobile sticky filter bar
  // where vertical and horizontal space is tight.
  if (compact) {
    return (
      <Box
        ref={scrollerRef}
        sx={{
          display: "flex",
          gap: 1,
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none"
        }}
      >
        {dates.map((date) => {
          const selectable = isSelectable(date);
          const selected = isSelected(date);
          const today = isToday(date);
          return (
            <ButtonBase
              key={date.toISODate()}
              ref={selected ? selectedChipRef : null}
              onClick={() => handleSelect(date)}
              disabled={!selectable}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "2.875rem",
                py: 0.75,
                borderRadius: 2,
                border: "1.5px solid",
                borderColor: selected ? "primary.main" : today ? "secondary.main" : "divider",
                bgcolor: selected ? "primary.main" : "#fff",
                color: selected ? "#fff" : selectable ? "text.primary" : "text.disabled",
                opacity: selectable ? 1 : 0.4,
                transition: "all 0.15s ease",
                flexShrink: 0
              }}
            >
              {/* Day and month are a step heavier than the number: at this size
                  700 reads as regular, and they should look as bold as it does. */}
              <Typography
                sx={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontSize: "0.55rem",
                  lineHeight: 1,
                  mb: 0.5,
                  color: "inherit"
                }}
              >
                {date.toFormat("ccc")}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1, color: "inherit" }}>{date.day}</Typography>
              <Typography
                sx={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontSize: "0.5rem",
                  lineHeight: 1,
                  mt: 0.35,
                  color: "inherit"
                }}
              >
                {date.toFormat("LLL")}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    );
  }

  const chevronSize = dense ? 28 : 32;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: dense ? 1.5 : 2 }}>
      {/* The visible range, not the selected date: paging moves the strip and
          left the heading behind, so four arrows in it still read "31 Aug"
          while the chips were showing October. */}
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ whiteSpace: "nowrap", flexShrink: 0, letterSpacing: "-0.01em", fontSize: dense ? "0.95rem" : undefined }}
      >
        {pageLabel}
      </Typography>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: dense ? 1 : 1.5, minWidth: 0 }}>
        <IconButton
          size="small"
          onClick={handlePrev}
          disabled={!canGoBack}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: "50%", width: chevronSize, height: chevronSize, flexShrink: 0 }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            gap: dense ? 1 : 1.5,
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none"
          }}
        >
          {dates.map((date) => {
            const selectable = isSelectable(date);
            const selected = isSelected(date);
            const today = isToday(date);

            return (
              <ButtonBase
                key={date.toISODate()}
                onClick={() => handleSelect(date)}
                disabled={!selectable}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: dense ? 52 : 70,
                  py: dense ? 1.1 : 2,
                  borderRadius: dense ? 2 : 3,
                  border: "1.5px solid",
                  borderColor: selected ? "primary.main" : today ? "secondary.main" : "divider",
                  bgcolor: selected ? "primary.main" : "#fff",
                  color: selected ? "#fff" : selectable ? "text.primary" : "text.disabled",
                  opacity: selectable ? 1 : 0.45,
                  transition: "all 0.15s ease",
                  flexShrink: 0,
                  "&:hover": selectable && !selected ? { borderColor: "primary.main" } : {}
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: dense ? "0.58rem" : "0.625rem",
                    lineHeight: 1,
                    mb: dense ? 0.4 : 0.75,
                    color: "inherit"
                  }}
                >
                  {date.toFormat("ccc")}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: dense ? "1rem" : "1.25rem", lineHeight: 1, color: "inherit" }}>{date.day}</Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontSize: dense ? "0.5rem" : "0.5625rem",
                    lineHeight: 1,
                    mt: dense ? 0.35 : 0.5,
                    color: "inherit"
                  }}
                >
                  {date.toFormat("LLL")}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        <IconButton
          size="small"
          onClick={handleNext}
          disabled={!canGoNext}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: "50%", width: chevronSize, height: chevronSize, flexShrink: 0 }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

export default DateSelector;
